import { FC, useContext } from "react";
import { Controller } from "react-hook-form";
import { AsyncSelect, Form, FormField, ModalDialog, Numeric } from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useI18n from "../../../../hooks/useTranslations";
import { ConnectorFamily } from "../../../../http/deckDesigner/types/connectorFamilies";
import {
  ConnectorMapping,
  InsertConnectorMappingBody,
  UpdateConnectorMappingBody,
} from "../../../../http/deckDesigner/types/connectorMappings";
import { FasteningType } from "../../../../http/deckDesigner/types/fasteningTypes";
import { GroovingType } from "../../../../http/deckDesigner/types/groovingTypes";
import { SecondaryItem } from "../../../../http/deckDesigner/types/secondaryItems";
import { SubstructureMaterial } from "../../../../http/deckDesigner/types/substructureMaterials";
import useDeckConnectorFamiliesApi from "../../../../http/deckDesigner/useConnectorFamilies";
import useDeckConnectorMappingsApi from "../../../../http/deckDesigner/useConnectorMappings";
import useDeckFasteningTypesApi from "../../../../http/deckDesigner/useFasteningTypes";
import useDeckGroovingTypesApi from "../../../../http/deckDesigner/useGroovingTypes";
import useDeckSecondaryItemsApi from "../../../../http/deckDesigner/useSecondaryItems";
import useDeckSubstructureMaterialsApi from "../../../../http/deckDesigner/useSubstructureMaterials";
import { getItemOptionLabel } from "../../../../hooks/useItemOptions";

interface ConnectorMappingFormBody {
  substructureMaterials: SubstructureMaterial[];
  fasteningType: FasteningType | null;
  groovingType: GroovingType | null;
  minimumGap: number | null;
  maximumGap: number | null;
  connectorFamily: ConnectorFamily | null;
  secondaryItem: SecondaryItem | null;
}

interface ConnectorMappingFormProps {
  connectorMapping?: ConnectorMapping;
  onClose: () => void;
}

const ConnectorMappingForm: FC<ConnectorMappingFormProps> = ({ connectorMapping, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const connectorMappingsApi = useDeckConnectorMappingsApi();
  const substructureMaterialsApi = useDeckSubstructureMaterialsApi();
  const fasteningTypesApi = useDeckFasteningTypesApi();
  const groovingTypesApi = useDeckGroovingTypesApi();
  const connectorFamiliesApi = useDeckConnectorFamiliesApi();
  const secondaryItemsApi = useDeckSecondaryItemsApi();
  const substructureMaterialOptions = useAsyncOptions(substructureMaterialsApi.getDeckSubstructureMaterials);
  const fasteningTypeOptions = useAsyncOptions(fasteningTypesApi.getDeckFasteningTypes);
  const groovingTypeOptions = useAsyncOptions(groovingTypesApi.getDeckGroovingTypes);
  const connectorFamilyOptions = useAsyncOptions(connectorFamiliesApi.getDeckConnectorFamilies);
  const secondaryItemOptions = useAsyncOptions(secondaryItemsApi.getDeckSecondaryItems);

  const defaultValues: ConnectorMappingFormBody = {
    substructureMaterials: connectorMapping?.substructureMaterials ?? [],
    fasteningType: connectorMapping?.fasteningType ?? null,
    groovingType: connectorMapping?.groovingType ?? null,
    minimumGap: connectorMapping?.minimumGap ?? null,
    maximumGap: connectorMapping?.maximumGap ?? null,
    connectorFamily: connectorMapping?.connectorFamily ?? null,
    secondaryItem: connectorMapping?.secondaryItem ?? null,
  };

  const onSubmit = () => async (formData: ConnectorMappingFormBody) => {
    if (
      !formData.substructureMaterials.length ||
      !formData.fasteningType ||
      !formData.groovingType ||
      formData.minimumGap == null ||
      formData.maximumGap == null ||
      !formData.connectorFamily
    ) {
      return;
    }

    const body: InsertConnectorMappingBody | UpdateConnectorMappingBody = {
      substructureMaterialCodes: formData.substructureMaterials.map((m) => m.code),
      fasteningTypeCode: formData.fasteningType.code,
      groovingTypeCode: formData.groovingType.code,
      minimumGap: formData.minimumGap,
      maximumGap: formData.maximumGap,
      connectorFamilyCode: formData.connectorFamily.code,
      secondaryItemId: formData.secondaryItem?.item.id ?? null,
    };

    try {
      if (connectorMapping) {
        await connectorMappingsApi.updateDeckConnectorMapping(connectorMapping.id, body);
      } else {
        await connectorMappingsApi.insertDeckConnectorMapping(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        connectorMapping
          ? `${i18n.translation.common.edit} ${i18n.translation.deckDesignerModule.connectorMappings.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.deckDesignerModule.connectorMappings.singular}`
      }
    >
      <Form<ConnectorMappingFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            <Controller
              name="substructureMaterials"
              control={control}
              rules={{ validate: (value) => !!value.length }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.substructureMaterials.plural}
                  error={fieldState.error}
                >
                  {({ labelId }) => (
                    <AsyncSelect<SubstructureMaterial, true>
                      {...substructureMaterialOptions}
                      inputId={labelId}
                      value={field.value}
                      isMulti={true}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.code}
                      isInvalid={fieldState.invalid}
                      onChange={(options) => field.onChange(Array.from(options))}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="fasteningType"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.deckDesignerModule.fasteningTypes.singular} error={fieldState.error}>
                  {({ labelId }) => (
                    <AsyncSelect<FasteningType, false>
                      {...field}
                      {...fasteningTypeOptions}
                      inputId={labelId}
                      isMulti={false}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.code}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="groovingType"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.deckDesignerModule.groovingTypes.singular} error={fieldState.error}>
                  {({ labelId }) => (
                    <AsyncSelect<GroovingType, false>
                      {...field}
                      {...groovingTypeOptions}
                      inputId={labelId}
                      isMulti={false}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.code}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="minimumGap"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.connectorMappings.minimumGap}
                  error={fieldState.error}
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="maximumGap"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.connectorMappings.maximumGap}
                  error={fieldState.error}
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="connectorFamily"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.connectorFamilies.singular}
                  error={fieldState.error}
                >
                  {({ labelId }) => (
                    <AsyncSelect<ConnectorFamily, false>
                      {...field}
                      {...connectorFamilyOptions}
                      inputId={labelId}
                      isMulti={false}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.code}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="secondaryItem"
              control={control}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.deckDesignerModule.secondaryItems.singular}
                  error={fieldState.error}
                  isOptional
                >
                  {({ labelId }) => (
                    <AsyncSelect<SecondaryItem, false>
                      {...field}
                      {...secondaryItemOptions}
                      inputId={labelId}
                      isMulti={false}
                      isClearable
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => getItemOptionLabel(option.item)}
                      getOptionValue={(option) => option.item.id}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option ?? null)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default ConnectorMappingForm;
